import React from "react";
import { sources } from "../data/snapshots.js";
import { round } from "../science/kernel.mjs";
import {
  BUILD_INFO,
  DataTable,
  JsonExportButton,
  Metric,
  PageHeader,
  ReviewActionRow,
  StatusPill,
} from "../components/ui.jsx";

export function ClaimsRegister({
  claims: claimRows,
  selectedClaim,
  selectedClaimId,
  setSelectedClaimId,
  filters,
  setFilters,
  recordApproval,
}) {
  const statusOptions = ["All", ...new Set(claimRows.map((claim) => claim.status))];
  const domainOptions = ["All", ...new Set(claimRows.map((claim) => claim.domain))];
  const confidenceOptions = ["All", "0-40%", "40-70%", "70-90%", "90%+"];

  const sourceTypeOptions = [
    "All",
    ...new Set(
      claimRows.flatMap((claim) =>
        claim.sourceIds.map((sourceId) => sourceById(sourceId)?.trust).filter(Boolean)
      )
    ),
  ];
  const recencyOptions = ["All", "Last 24h", "Last 7d", "Last 30d", "Older"];

  const filtered = claimRows.filter((claim) => {
    if (filters.status !== "All" && claim.status !== filters.status) return false;
    if (filters.domain !== "All" && claim.domain !== filters.domain) return false;
    if (filters.search && !`${claim.claim} ${claim.notes}`.toLowerCase().includes(filters.search.toLowerCase())) return false;

    const confidence = claim.confidence * 100;
    if (filters.confidence === "0-40%" && confidence > 40) return false;
    if (filters.confidence === "40-70%" && (confidence < 40 || confidence > 70)) return false;
    if (filters.confidence === "70-90%" && (confidence < 70 || confidence > 90)) return false;
    if (filters.confidence === "90%+" && confidence < 90) return false;

    if (filters.sourceType !== "All") {
      const trusts = claim.sourceIds.map((sourceId) => sourceById(sourceId)?.trust).filter(Boolean);
      if (!trusts.includes(filters.sourceType)) return false;
    }

    if (filters.recency !== "All") {
      const age = Math.floor((Date.now() - new Date(claim.lastVerified).getTime()) / (1000 * 60 * 60 * 24));
      if (filters.recency === "Last 24h" && age > 1) return false;
      if (filters.recency === "Last 7d" && age > 7) return false;
      if (filters.recency === "Last 30d" && age > 30) return false;
    }

    return true;
  });

  const statusCounts = statusOptions.slice(1).map((status) => ({
    status,
    count: claimRows.filter((claim) => claim.status === status).length,
  }));
  const highImpactUnverified = claimRows.filter(
    (claim) => claim.status !== "Verified" && claim.impact === "High"
  ).length;

  function buildClaimsLedgerExport() {
    return {
      build: BUILD_INFO,
      exportedAt: new Date().toISOString(),
      filters,
      selectedClaimId,
      visibleClaims: filtered,
      totals: {
        allClaims: claimRows.length,
        visibleClaims: filtered.length,
        highImpactUnverified,
        statusCounts,
      },
    };
  }

  return (
    <section className="module">
      <div className="claims-header">
        <PageHeader
          title="CLAIMS REGISTER"
          muted="Evidence control"
          subtitle={`Data cut: ${new Date().toISOString().slice(0, 10)}`}
        />
        <JsonExportButton
          filename={`inner-solar-claims-ledger-${BUILD_INFO.id}.json`}
          createPayload={buildClaimsLedgerExport}
        >
          Export claims ledger
        </JsonExportButton>
      </div>

      <article className="panel">
        <div className="panel-topline">
          <h2>Filters</h2>
          <StatusPill tone="blue">audit ready</StatusPill>
        </div>
        <div className="filter-grid">
          <label>
            Status
            <select
              value={filters.status}
              onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
            >
              {statusOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Domain
            <select
              value={filters.domain}
              onChange={(event) => setFilters((current) => ({ ...current, domain: event.target.value }))}
            >
              {domainOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Confidence
            <select
              value={filters.confidence}
              onChange={(event) => setFilters((current) => ({ ...current, confidence: event.target.value }))}
            >
              {confidenceOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Source type
            <select
              value={filters.sourceType}
              onChange={(event) => setFilters((current) => ({ ...current, sourceType: event.target.value }))}
            >
              {sourceTypeOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Recency
            <select
              value={filters.recency}
              onChange={(event) => setFilters((current) => ({ ...current, recency: event.target.value }))}
            >
              {recencyOptions.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          <label>
            Search
            <input
              type="search"
              value={filters.search}
              onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            />
          </label>
        </div>
      </article>

      <div className="module-grid">
        <article className="panel panel-large">
          <div className="panel-topline">
            <h2>Claims table</h2>
            <StatusPill tone="green">{filtered.length} visible</StatusPill>
          </div>
          <DataTable
            columns={[
              { key: "claim", header: "Claim" },
              { key: "domain", header: "Domain" },
              {
                key: "status",
                header: "Status",
                render: (claim) => (
                  <StatusPill tone={claimStatusTone(claim.status)}>
                    {claim.status}
                  </StatusPill>
                ),
              },
              {
                key: "confidence",
                header: "Confidence",
                render: (claim) => `${round(claim.confidence * 100, 0)}%`,
              },
              {
                key: "source",
                header: "Source",
                render: (claim) =>
                  (claim.sourceIds || [])
                    .map((sourceId) => sourceById(sourceId)?.trust ?? "unscored")
                    .join(", "),
              },
              { key: "impact", header: "Impact" },
            ]}
            rows={filtered}
            getRowKey={(claim) => claim.id}
            getRowClassName={(claim) => (claim.id === selectedClaimId ? "is-active" : "")}
            onRowClick={(claim) => setSelectedClaimId(claim.id)}
          />
        </article>
        <article className="panel">
          <div className="panel-topline">
            <h2>Selected claim</h2>
            <StatusPill tone={selectedClaim.status === "Verified" ? "green" : "blue"}>
              {selectedClaim.status}
            </StatusPill>
          </div>
          <p>{selectedClaim.claim}</p>
          <Metric label="Confidence" value={`${round(selectedClaim.confidence * 100, 0)}%`} />
          <Metric label="Impact" value={selectedClaim.impact} />
          <p>{selectedClaim.notes}</p>
          <div className="source-chips">
            {selectedClaim.sourceIds?.map((sourceId) => {
              const source = sourceById(sourceId);
              return (
                <a key={sourceId} href={source?.url} target="_blank" rel="noreferrer">
                  {source?.title ?? sourceId}
                </a>
              );
            })}
          </div>
          <ReviewActionRow
            target={selectedClaim.claim}
            recordApproval={recordApproval}
            actions={[
              {
                label: "Promote",
                action: "promote",
                variant: "primary",
              },
              { label: "Request source", action: "request-source" },
              { label: "Reject", action: "reject" },
            ]}
          />
        </article>
      </div>

      <div className="claims-summary-grid">
        {statusCounts.map((row) => (
          <article className="panel" key={row.status}>
            <div className="panel-topline">
              <h2>{row.status}</h2>
              <StatusPill tone={row.status === "Verified" ? "green" : "blue"}>{row.count}</StatusPill>
            </div>
            <p>
              {row.status === "Verified"
                ? "Promoted claims with source-backed evidence."
                : "Claims awaiting or retaining status checks."}
            </p>
          </article>
        ))}
        <article className="panel">
          <div className="panel-topline">
            <h2>High-impact unverified</h2>
            <StatusPill tone="red">{highImpactUnverified}</StatusPill>
          </div>
          <p>Unverified high-impact claims should be treated as blockers for commercialization decisions.</p>
        </article>
      </div>
    </section>
  );
}

function sourceById(id) {
  return sources.find((source) => source.id === id);
}

function claimStatusTone(status) {
  if (status === "Verified") return "green";
  if (status === "Likely") return "blue";
  if (status === "Hypothesis") return "amber";
  return "red";
}
